import React, { useState, useEffect } from 'react';
import api, { UPLOADS_URL } from '../api';

function EventsPage() {
  const [events, setEvents] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);

  useEffect(() => {
    api.get('/api/public/events')
      .then(res => {
        setEvents(res.data);
        setFiltered(res.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    let result = events;
    if (search) {
      const s = search.toLowerCase();
      result = result.filter(e =>
        e.event_name?.toLowerCase().includes(s) ||
        e.event_city?.toLowerCase().includes(s) ||
        e.event_state?.toLowerCase().includes(s) ||
        e.event_date?.includes(s) ||
        e.organized_by?.toLowerCase().includes(s)
      );
    }
    if (stateFilter) result = result.filter(e => e.event_state === stateFilter);
    if (selectedCalendarDate) result = result.filter(e => {
  if (!e.event_end_date) return e.event_date === selectedCalendarDate;
  return selectedCalendarDate >= e.event_date && selectedCalendarDate <= e.event_end_date;
});
    setFiltered(result);
  }, [search, stateFilter, events, selectedCalendarDate]);

  const states = [...new Set(events.map(e => e.event_state))].filter(Boolean).sort();

  const isPast = (dateStr, endDateStr) => {
    const checkDate = endDateStr || dateStr;
    return new Date(checkDate + 'T23:59:59') < new Date();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const [y, m, d] = dateStr.split('-');
    const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    return `${months[parseInt(m) - 1]} ${parseInt(d)}, ${y}`;
  };

  const formatDateRange = (startStr, endStr) => {
    if (!endStr) return formatDate(startStr);
    const [sy, sm, sd] = startStr.split('-');
    const [ey, em, ed] = endStr.split('-');
    const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    if (sy === ey && sm === em) {
      return `${months[parseInt(sm)-1]} ${parseInt(sd)}–${parseInt(ed)}, ${sy}`;
    }
    return `${formatDate(startStr)} – ${formatDate(endStr)}`;
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [h, m] = timeStr.split(':');
    const hour = parseInt(h);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${m} ${ampm}`;
  };

  const getCalendarDays = () => {
    const year = calendarDate.getFullYear();
    const month = calendarDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const daysInPrevMonth = new Date(year, month, 0).getDate();
    const days = [];

    for (let i = firstDay - 1; i >= 0; i--) {
      days.push({ day: daysInPrevMonth - i, currentMonth: false });
    }
    for (let i = 1; i <= daysInMonth; i++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`;
      const hasEvents = events.some(e => {
  if (!e.event_end_date) return e.event_date === dateStr;
  return dateStr >= e.event_date && dateStr <= e.event_end_date;
});
      days.push({ day: i, currentMonth: true, dateStr, hasEvents });
    }
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({ day: i, currentMonth: false });
    }
    return days;
  };

  const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  if (loading) return <div className="loading">Loading events...</div>;

  if (selectedEvent) {
    return (
      <div className="events-page">
        <button
          onClick={() => setSelectedEvent(null)}
          style={{ marginBottom: '1rem', padding: '0.5rem 1rem', background: '#3d2a1a', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '2px', cursor: 'pointer', fontFamily: 'Cinzel, serif' }}
        >
          ← Back to Events
        </button>
        <div style={{ background: 'var(--dark-2)', border: '1px solid var(--border)', borderRadius: '4px', padding: '2rem', opacity: isPast(selectedEvent.event_date, selectedEvent.event_end_date) ? 0.8 : 1 }}>
          {isPast(selectedEvent.event_date, selectedEvent.event_end_date) && (
            <div style={{ padding: '0.5rem 1rem', background: '#2a2a2a', border: '1px solid #444', borderRadius: '2px', marginBottom: '1rem', color: '#888', fontFamily: 'Cinzel, serif', fontSize: '0.85rem' }}>
              📅 This event has already taken place
            </div>
          )}

          {selectedEvent.photos && selectedEvent.photos.length > 0 && (
            <div style={{ marginBottom: '1.5rem' }}>
              <img src={`${UPLOADS_URL}/${selectedEvent.photos[0]}`} alt={selectedEvent.event_name}
                style={{ width: '100%', maxHeight: '400px', objectFit: 'contain', borderRadius: '4px', border: '1px solid var(--border)' }} />
              {selectedEvent.photos.length > 1 && (
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                  {selectedEvent.photos.slice(1).filter(Boolean).map((photo, i) => (
                    <img key={i} src={`${UPLOADS_URL}/${photo}`} alt={`Event photo ${i + 2}`}
                      style={{ width: '150px', height: '150px', objectFit: 'cover', borderRadius: '2px', border: '1px solid var(--border)' }} />
                  ))}
                </div>
              )}
            </div>
          )}

          <h1 style={{ marginTop: 0 }}>{selectedEvent.event_name}</h1>
          <p style={{ color: 'var(--primary-light)', fontFamily: 'Cinzel, serif' }}>📅 {formatDateRange(selectedEvent.event_date, selectedEvent.event_end_date)}</p>
          {selectedEvent.event_start_time && (
            <p style={{ color: 'var(--text-muted)' }}>
              🕐 {formatTime(selectedEvent.event_start_time)}
              {selectedEvent.event_end_time && ` – ${formatTime(selectedEvent.event_end_time)}`}
            </p>
          )}
          <p style={{ color: 'var(--text-muted)' }}>📍 {selectedEvent.event_location_name}</p>
          {selectedEvent.event_location_address && <p style={{ color: 'var(--text-muted)' }}>{selectedEvent.event_location_address}</p>}
          <p style={{ color: 'var(--text-muted)' }}>{selectedEvent.event_city}, {selectedEvent.event_state}</p>
          <p><strong>Organized by:</strong> {selectedEvent.organized_by}</p>
          {selectedEvent.website_link && <p><strong>More Info:</strong> <a href={selectedEvent.website_link} target="_blank" rel="noopener noreferrer">{selectedEvent.website_link}</a></p>}

          <h3>Event Details</h3>
          <p style={{ whiteSpace: 'pre-wrap', lineHeight: '1.8' }}>{selectedEvent.event_details}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="events-page">
      <h1>Krampus Events</h1>

      <div className="calendar">
        <div className="calendar-header">
          <button
            onClick={() => setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() - 1))}
            style={{ background: 'none', border: '1px solid var(--border)', color: 'var(--text)', padding: '0.3rem 0.8rem', cursor: 'pointer', borderRadius: '2px' }}
          >◀</button>
          <h3>{monthNames[calendarDate.getMonth()]} {calendarDate.getFullYear()}</h3>
          <button
            onClick={() => setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() + 1))}
            style={{ background: 'none', border: '1px solid var(--border)', color: 'var(--text)', padding: '0.3rem 0.8rem', cursor: 'pointer', borderRadius: '2px' }}
          >▶</button>
        </div>

        {selectedCalendarDate && (
          <div style={{ textAlign: 'center', marginBottom: '0.5rem' }}>
            <span style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
              Showing events on {formatDate(selectedCalendarDate)}
            </span>
            <button
              onClick={() => setSelectedCalendarDate(null)}
              style={{ marginLeft: '0.5rem', background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontSize: '0.9rem' }}
            >
              (clear)
            </button>
          </div>
        )}

        <div className="calendar-grid">
          {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
            <div key={d} className="calendar-day-header">{d}</div>
          ))}
          {getCalendarDays().map((day, i) => (
            <div
              key={i}
              className={`calendar-day ${day.hasEvents ? 'has-events' : ''} ${day.dateStr === todayStr ? 'today' : ''} ${!day.currentMonth ? 'other-month' : ''}`}
              onClick={() => day.hasEvents && day.dateStr && setSelectedCalendarDate(day.dateStr === selectedCalendarDate ? null : day.dateStr)}
              title={day.hasEvents ? `Events on ${formatDate(day.dateStr)}` : ''}
            >
              {day.day}
            </div>
          ))}
        </div>
      </div>

      <div className="search-bar">
        <input
          type="text"
          placeholder="Search by name, city, state, or date..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select value={stateFilter} onChange={e => setStateFilter(e.target.value)}>
          <option value="">All States/Provinces</option>
          {states.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <button
          onClick={() => { setSearch(''); setStateFilter(''); setSelectedCalendarDate(null); }}
          style={{ padding: '0.6rem 1rem', background: '#3d2a1a', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '2px', cursor: 'pointer', fontFamily: 'Cinzel, serif', fontSize: '0.85rem' }}
        >
          Clear
        </button>
      </div>

      <p style={{ color: 'var(--text-muted)', marginBottom: '1.5rem' }}>
        {filtered.length === 0 ? 'No events found.' : `Showing ${filtered.length} event${filtered.length !== 1 ? 's' : ''}`}
      </p>

      {filtered.map(event => (
        <div
          key={event.id}
          className={`event-card ${isPast(event.event_date, event.event_end_date) ? 'past-event' : ''}`}
          onClick={() => setSelectedEvent(event)}
        >
          {event.photos && event.photos[0] ? (
            <img src={`${UPLOADS_URL}/${event.photos[0]}`} alt={event.event_name}
              style={{ width: '200px', minHeight: '150px', objectFit: 'contain', background: 'var(--dark-3)' }} />
          ) : (
            <div style={{ width: '200px', minHeight: '150px', background: 'var(--dark-3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '3rem', color: 'var(--border)' }}>
              📅
            </div>
          )}
          <div className="event-card-body">
            {isPast(event.event_date, event.event_end_date) && (
              <span style={{ fontSize: '0.75rem', color: '#888', fontFamily: 'Cinzel, serif', letterSpacing: '0.05em' }}>PAST EVENT • </span>
            )}
            <div className="event-date">📅 {formatDateRange(event.event_date, event.event_end_date)}</div>
            <h3 className="event-title">{event.event_name}</h3>
            <p className="event-location">📍 {event.event_city}, {event.event_state}</p>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>Organized by: {event.organized_by}</p>
          </div>
        </div>
      ))}

      {filtered.length === 0 && !loading && (
        <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
          <p>No events found. <a href="/register-event">Submit an event!</a></p>
        </div>
      )}
    </div>
  );
}

export default EventsPage;
